/**
 * Tooltip Component
 *
 * A polished tooltip component for displaying contextual information.
 * Follows Fortune 100 best practices with proper positioning, delays, and accessibility.
 *
 * Features:
 * - Configurable position (right, top, bottom, left)
 * - Customizable delay before showing
 * - Proper accessibility with role="tooltip"
 * - Smooth animations
 * - Auto-positioning to stay within viewport
 *
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {string} content - The tooltip text content
 * @param {string} position - Position relative to trigger: 'right' | 'top' | 'bottom' | 'left'
 * @param {number} delay - Delay in ms before showing tooltip (default: 200)
 * @param {boolean} disabled - If true, tooltip won't show
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import "./Tooltip.css";

function Tooltip({
  children,
  content,
  position = "right",
  delay = 200,
  disabled = false,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  // Calculate tooltip position based on trigger element
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8; // Space between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + padding;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - padding;
        break;
      case "top":
        top = triggerRect.top - tooltipRect.height - padding;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + padding;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      default:
        break;
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }

    // Adjust vertical position
    if (top < padding) {
      top = padding;
    } else if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  // Handle mouse enter with delay
  const handleMouseEnter = useCallback(() => {
    if (disabled || !content) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [disabled, content, delay]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Calculate position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure tooltip is rendered before calculating position
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [isVisible, calculatePosition]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle keyboard focus for accessibility
  const handleFocus = useCallback(() => {
    if (disabled || !content) return;
    setIsVisible(true);
  }, [disabled, content]);

  const handleBlur = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <div
      className="tooltip-wrapper"
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {children}
      {isVisible && content && (
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${position}`}
          role="tooltip"
          aria-hidden={!isVisible}
          style={{
            position: "fixed",
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="tooltip-content">{content}</div>
          <div className={`tooltip-arrow tooltip-arrow-${position}`} />
        </div>
      )}
    </div>
  );
}

export default Tooltip;
